import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import Api_key from './apiconfiguration';
import { watchedKeys } from './discoveryModel';

export const deckKey = item => `${item.media_type || item.mediaType || (item.title ? 'movie' : 'tv')}_${item.id}`;
export function uniqueDeck(items, excluded = new Set()) {
  const seen = new Set(excluded);
  return items.filter(item => {
    const key = deckKey(item);
    if (!item.id || !item.poster_path || item.adult || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
function readHistory(key) {
  try { const value = JSON.parse(localStorage.getItem(key) || '[]'); return Array.isArray(value) ? value.filter(v => typeof v === 'string') : []; }
  catch { return []; }
}

export default function useOrbitDeck({ userId, library = [], experience = {} }) {
  const storageKey = `hedone-orbit-seen:${userId || 'guest'}`;
  const [history, setHistory] = useState(() => readHistory(storageKey));
  const historyRef = useRef(history);
  const [items, setItems] = useState([]), [mode, setMode] = useState('all');
  const [page, setPage] = useState(1), [attempt, setAttempt] = useState(0);
  const [busy, setBusy] = useState(false), [error, setError] = useState(''), [more, setMore] = useState(true);
  const [lastPassed, setLastPassed] = useState(null);
  const automaticPages = useRef(0);
  useEffect(() => { historyRef.current = readHistory(storageKey); setHistory(historyRef.current); setLastPassed(null); }, [storageKey]);
  const excluded = useMemo(() => {
    const keys = new Set([...history, ...watchedKeys(experience.journal)]);
    library.filter(item => item.liked || item.watchlist).forEach(item => keys.add(deckKey(item)));
    Object.values(experience.diary || {}).forEach(item => keys.add(deckKey(item)));
    Object.values(experience.progress || {}).filter(item => item.completed).forEach(item => keys.add(deckKey(item)));
    return keys;
  }, [history, library, experience.journal, experience.diary, experience.progress]);
  const queue = useMemo(() => uniqueDeck(items, excluded).filter(item => mode === 'all' || item.media_type === mode), [items, excluded, mode]);
  useEffect(() => {
    if (!userId) return;
    const controller = new AbortController();
    setBusy(true); setError('');
    const types = mode === 'all' ? ['movie', 'tv'] : [mode];
    Promise.all(types.map(type => axios.get(`https://api.themoviedb.org/3/discover/${type}`, {
      params:{api_key:Api_key, page, include_adult:false, sort_by:'popularity.desc', 'vote_count.gte':50},
      signal:controller.signal, timeout:12000,
    }).then(({data}) => ({type, data})))).then(results => {
      if (controller.signal.aborted) return;
      const batches = results.map(({type,data}) => (data.results || []).map(item => ({...item,media_type:type})));
      const mixed = Array.from({length:Math.max(0,...batches.map(batch => batch.length))}, (_,i) => batches.map(batch => batch[i]).filter(Boolean)).flat();
      setItems(previous => uniqueDeck([...previous,...mixed]));
      setMore(results.some(({data}) => page < Math.min(data.total_pages || 1, 500)));
    }).catch(() => { if (!controller.signal.aborted) setError('Could not load new discoveries. Your place is saved.'); })
      .finally(() => { if (!controller.signal.aborted) setBusy(false); });
    return () => controller.abort();
  }, [userId, mode, page, attempt]);
  useEffect(() => {
    if (!busy && !error && more && items.length && queue.length < 8 && automaticPages.current < 4) {
      automaticPages.current += 1;
      setPage(value => value + 1);
    }
  }, [busy, error, more, items.length, queue.length]);
  const persist = useCallback(next => {
    const value = typeof next === 'function' ? next(historyRef.current) : next;
    historyRef.current = value;
    setHistory(value);
    try { localStorage.setItem(storageKey, JSON.stringify(value)); } catch { /* In-memory history still works when storage is unavailable. */ }
  }, [storageKey]);
  const consume = (item, passed = false) => {
    automaticPages.current = 0;
    persist(previous => [...new Set([...previous, deckKey(item)])]);
    setLastPassed(passed ? item : null);
  };
  return { queue, mode, busy, error, more, reviewed:history.length, lastPassed,
    changeMode(value) { setMode(value); setItems([]); setPage(1); setMore(true); setError(''); setLastPassed(null); automaticPages.current = 0; },
    consume,
    restore(item) { persist(previous => previous.filter(key => key !== deckKey(item))); setItems(previous => [...previous.filter(entry => deckKey(entry) !== deckKey(item)), item]); },
    undo() { if (!lastPassed) return; persist(previous => previous.filter(key => key !== deckKey(lastPassed))); setItems(previous => uniqueDeck([lastPassed,...previous])); setLastPassed(null); },
    loadMore() { automaticPages.current = 0; if(error) setAttempt(value => value+1); else setPage(value => value+1); },
  };
}
